import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCheckout } from "@/hooks/useCheckout";
import { Reveal } from "@/components/Motion";
import { Extras } from "@/components/pricing/Extras";
import { Check, Loader2, ArrowRight } from "lucide-react";

const FEATURES = ["1 car live at a time", "Reg lookup fills the details", "Direct buyer messaging", "Keep 100% of your sale", "Cancel anytime"];
const fmt = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

function PlanCard({ sub, busy, onSubscribe }) {
  const free = sub?.free_until;
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} whileHover={{ y: -6 }}
      className="bg-navy text-white rounded-3xl p-8 lg:p-10" data-testid="private-plan-card">
      <span className="text-xs uppercase tracking-widest font-semibold text-white/60">Private seller</span>
      <div className="mt-5 grid grid-cols-2 gap-6">
        <div><div className="text-white/60 text-sm">First 3 months</div><div className="font-heading font-extrabold text-5xl tracking-tighter mt-1" data-testid="private-free-price">Free</div></div>
        <div><div className="text-white/60 text-sm">After that</div><div className="font-heading font-extrabold text-5xl tracking-tighter mt-1" data-testid="private-monthly-price">£9.99<span className="text-base font-medium text-white/60">/mo</span></div></div>
      </div>
      <ul className="mt-8 space-y-3">{FEATURES.map((f) => <li key={f} className="flex gap-2.5 text-sm"><Check className="w-4 h-4 text-[#9CCBD7] mt-0.5" />{f}</li>)}</ul>
      {sub?.plan === "private_monthly" ? (
        <p className="mt-8 text-sm bg-white/10 rounded-full px-4 py-2 inline-flex items-center gap-2" data-testid="private-active"><Check className="w-4 h-4" />Your £9.99 plan is active</p>
      ) : (
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/sell" data-testid="private-list-free" className="h-12 px-7 rounded-full bg-teal hover:bg-[#347a8c] font-semibold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5">List my car free <ArrowRight className="w-4 h-4" /></Link>
          <button onClick={onSubscribe} disabled={!!busy} data-testid="checkout-private_monthly" className="h-12 px-7 rounded-full border border-white/30 hover:bg-white/10 font-semibold inline-flex items-center justify-center">
            {busy === "private_monthly" ? <Loader2 className="w-4 h-4 animate-spin" /> : free ? "Set up £9.99 for later" : "Subscribe £9.99/mo"}
          </button>
        </div>
      )}
      {free && sub?.plan !== "private_monthly" && <p className="text-xs text-white/60 mt-4" data-testid="private-free-until">Your free period runs until {fmt(free)}. If you set up the plan now, your first charge is on that date.</p>}
    </motion.div>
  );
}

export default function PrivatePricing() {
  const { user } = useAuth();
  const { busy, checkout } = useCheckout();
  const [sub, setSub] = useState(null);
  useEffect(() => { if (user) api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {}); }, [user]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20" data-testid="private-pricing-page">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <span className="text-xs uppercase tracking-widest font-semibold text-teal">Private sellers</span>
          <h1 className="font-heading font-extrabold text-navy tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-2">Free to list.<br />For your first 3 months.</h1>
          <p className="text-slatev mt-5 text-base sm:text-lg max-w-md">New sellers list one car free for 3 months. After that it's £9.99 a month. No commission, ever.</p>
          {user?.seller_type === "dealer" && <p className="mt-4 text-sm text-amber-700" data-testid="private-dealer-note">You're signed in as a dealer. See dealer pricing instead.</p>}
        </Reveal>
        <PlanCard sub={sub} busy={busy} onSubscribe={() => checkout("private_monthly")} />
      </div>
      <Extras audience="private" />
      <p className="text-center text-xs text-slatev mt-16">Test mode: use card 4242 4242 4242 4242, any future expiry and CVC.</p>
    </div>
  );
}
